<template>
    <!-- eslint-disable-next-line @maltjoy/eslint-rules/no-html-button -->
    <button
        v-if="!custom"
        v-bind="buttonAttrs"
        class="uncrawlable-link"
        type="button"
        :disabled="disabled || undefined"
        :data-testid="dataTestid"
        @click="onClick"
        @auxclick="onAuxClick"
    >
        <slot :navigate="navigate" />
    </button>
    <slot v-else :navigate="navigate" />
</template>

<script setup lang="ts">
    import {computed, useAttrs} from 'vue';
    import type {RouteLocationRaw} from 'vue-router';
    import {navigateTo} from '#imports';

    /**
     * Same destination shape as NuxtLink, rendered as a button.
     * Crawlers discover URLs from `<a href>`. This control keeps `to` out of the HTML link graph
     * and navigates on click instead.
     */
    interface Props {
        to: RouteLocationRaw;
        /** Full page load. Absolute URLs (https, mailto, tel, protocol-relative) are external on their own. */
        external?: boolean;
        /** Replace the current history entry. Ignored when the click opens a new browsing context. */
        replace?: boolean;
        /** `_blank` opens a new tab without an href. Other browsing contexts are forwarded to `navigateTo`. */
        target?: string;
        disabled?: boolean;
        dataTestid?: string;
        /** Render nothing and pass `navigate` to the default slot. */
        custom?: boolean;
        /** Appended as `componentSource` query param on navigation for signup/analytics attribution. */
        dataComponentSource?: string;
    }

    const {to, external = false, replace = false, target, disabled = false, dataTestid, custom = false, dataComponentSource} = defineProps<Props>();

    defineSlots<{
        default(slotProps: {navigate: (event?: MouseEvent) => Promise<void>}): unknown;
    }>();

    defineOptions({
        inheritAttrs: false,
    });

    const PROTOCOL = /^[a-z][a-z\d+.-]*:/i;
    const UNSAFE_PROTOCOL = /^(?:javascript|data|vbscript):/i;

    const attrs = useAttrs();

    // Drop a fallthrough href so a mistaken attribute cannot put the URL back in the DOM.
    const buttonAttrs = computed(() => {
        const {href: _href, ...rest} = attrs;
        return rest;
    });

    function withComponentSource(href: string, source: string): string {
        const [baseHref, hash] = href.split('#');
        const separator = baseHref?.includes('?') ? '&' : '?';
        const hashParam = href.includes('#') ? `#${hash}` : '';
        return `${baseHref}${separator}componentSource=${source}${hashParam}`;
    }

    function navigationDestination(): RouteLocationRaw {
        const source = dataComponentSource;
        if (!source) {
            return to;
        }

        if (typeof to === 'string') {
            return withComponentSource(to, source);
        }

        const existingQuery = to.query && typeof to.query === 'object' && !Array.isArray(to.query) ? to.query : {};

        return {...to, query: {...existingQuery, componentSource: source}};
    }

    function isExternalDestination(destination: RouteLocationRaw): boolean {
        if (typeof destination !== 'string') {
            return false;
        }

        const value = destination.trim();
        return PROTOCOL.test(value) || value.startsWith('//');
    }

    function isUnsafeDestination(destination: RouteLocationRaw): boolean {
        return typeof destination === 'string' && UNSAFE_PROTOCOL.test(destination.trim());
    }

    function shouldOpenInNewContext(event: MouseEvent): boolean {
        const explicitNewContext = target !== undefined && target !== '_self';
        const modifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;

        return explicitNewContext || modifiedClick;
    }

    async function navigate(event?: MouseEvent) {
        if (disabled || isUnsafeDestination(to)) {
            return;
        }

        const destination = navigationDestination();
        const openInNewContext = event ? shouldOpenInNewContext(event) : target !== undefined && target !== '_self';
        const browsingTarget = target && target !== '_self' ? target : '_blank';

        await navigateTo(destination, {
            ...(external || isExternalDestination(destination) ? {external: true} : {}),
            ...(!openInNewContext && replace ? {replace: true} : {}),
            ...(openInNewContext ? {open: {target: browsingTarget}} : {}),
        });
    }

    function onClick(event: MouseEvent) {
        void navigate(event);
    }

    function onAuxClick(event: MouseEvent) {
        if (event.button !== 1) {
            return;
        }

        event.preventDefault();
        void navigate(event);
    }
</script>

<style lang="scss">
    // UA button chrome (border, background, font) does not match the link this control replaces.
    // :where() keeps specificity at 0 so a consumer class on the same node can still set color, padding, and type.
    :where(.uncrawlable-link) {
        appearance: none;
        background-color: transparent;
        border: 0;
        color: inherit;
        cursor: pointer;
        font: inherit;
        padding: 0;
        text-align: inherit;
    }

    :where(.uncrawlable-link:disabled) {
        cursor: not-allowed;
    }
</style>
